export type AppUpdateResult =
  | { status: 'activating' }
  | { status: 'blocked'; reason: string }
  | { status: 'unavailable' };

type Flusher = () => void | Promise<void>;
type Blocker = () => string | null;
type Activator = () => void | Promise<void>;

export interface AppUpdateCoordinator {
  registerFlusher: (key: string, flusher: Flusher) => () => void;
  registerBlocker: (key: string, blocker: Blocker) => () => void;
  setWaitingActivator: (activator: Activator | null) => void;
  hasWaitingUpdate: () => boolean;
  activate: (activator?: Activator) => Promise<AppUpdateResult>;
  handleControllerChange: (reload?: () => void) => Promise<AppUpdateResult>;
  allowActivationRetry: () => void;
  reloadOnce: (reload?: () => void) => boolean;
  subscribe: (listener: () => void) => () => void;
  getBlockedReason: () => string | null;
  clearBlockedReason: () => void;
}

export function createAppUpdateCoordinator(): AppUpdateCoordinator {
  const flushers = new Map<string, Flusher>();
  const blockers = new Map<string, Blocker>();
  const listeners = new Set<() => void>();
  let waitingActivator: Activator | null = null;
  let activation: Promise<AppUpdateResult> | null = null;
  let controllerTakeover: Promise<AppUpdateResult> | null = null;
  let activationStarted = false;
  let reloadStarted = false;
  let pendingControllerReload: (() => void) | null = null;
  let blockedReason: string | null = null;

  const setBlockedReason = (reason: string | null) => {
    if (blockedReason === reason) return;
    blockedReason = reason;
    listeners.forEach((listener) => listener());
  };

  const reloadOnce = (reload: () => void = () => window.location.reload()) => {
    if (reloadStarted) return false;
    reloadStarted = true;
    reload();
    return true;
  };

  const prepare = async (): Promise<AppUpdateResult | null> => {
    const flusherSnapshot = Array.from(flushers.values());
    const blockerSnapshot = Array.from(blockers.values());
    try {
      for (const flusher of flusherSnapshot) await flusher();
    } catch {
      const reason = 'Не удалось сохранить изменения. Повтори обновление.';
      setBlockedReason(reason);
      return { status: 'blocked', reason };
    }

    try {
      for (const blocker of blockerSnapshot) {
        const reason = blocker();
        if (reason) {
          setBlockedReason(reason);
          return { status: 'blocked', reason };
        }
      }
    } catch {
      const reason = 'Не удалось проверить несохранённые изменения. Повтори обновление.';
      setBlockedReason(reason);
      return { status: 'blocked', reason };
    }
    return null;
  };

  const activatePendingController = (): Promise<AppUpdateResult> => {
    if (reloadStarted) return Promise.resolve({ status: 'activating' });
    if (!pendingControllerReload) return Promise.resolve({ status: 'unavailable' });
    if (controllerTakeover) return controllerTakeover;

    const reload = pendingControllerReload;
    controllerTakeover = (async () => {
      const blocked = await prepare();
      if (blocked) {
        activationStarted = false;
        activation = null;
        return blocked;
      }

      setBlockedReason(null);
      reloadOnce(reload);
      if (pendingControllerReload === reload) pendingControllerReload = null;
      return { status: 'activating' };
    })();

    const currentTakeover = controllerTakeover;
    void currentTakeover.then(() => {
      if (controllerTakeover === currentTakeover) controllerTakeover = null;
    });
    return currentTakeover;
  };

  const activate = async (requestedActivator?: Activator): Promise<AppUpdateResult> => {
    if (pendingControllerReload) return activatePendingController();

    const activator = requestedActivator ?? waitingActivator ?? undefined;
    if (!activator) return { status: 'unavailable' };
    if (activationStarted) return { status: 'activating' };
    if (activation) return activation;

    activation = (async () => {
      const blocked = await prepare();
      if (blocked) return blocked;

      activationStarted = true;
      setBlockedReason(null);
      try {
        await activator();
        return { status: 'activating' };
      } catch {
        activationStarted = false;
        const reason = 'Обновление не запустилось. Попробуй ещё раз.';
        setBlockedReason(reason);
        return { status: 'blocked', reason };
      }
    })();

    const result = await activation;
    if (result.status === 'blocked') activation = null;
    return result;
  };

  const handleControllerChange = (reload: () => void = () => window.location.reload()) => {
    if (reloadStarted) return Promise.resolve<AppUpdateResult>({ status: 'activating' });
    if (!pendingControllerReload) pendingControllerReload = reload;
    return activatePendingController();
  };

  return {
    registerFlusher(key, flusher) {
      flushers.set(key, flusher);
      return () => {
        if (flushers.get(key) === flusher) flushers.delete(key);
      };
    },
    registerBlocker(key, blocker) {
      blockers.set(key, blocker);
      return () => {
        if (blockers.get(key) === blocker) blockers.delete(key);
      };
    },
    setWaitingActivator(activator) {
      waitingActivator = activator;
      if (!activator && !pendingControllerReload) setBlockedReason(null);
    },
    hasWaitingUpdate() {
      return waitingActivator !== null || pendingControllerReload !== null;
    },
    activate,
    handleControllerChange,
    allowActivationRetry() {
      if (reloadStarted || pendingControllerReload) return;
      activationStarted = false;
      activation = null;
    },
    reloadOnce,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getBlockedReason() {
      return blockedReason;
    },
    clearBlockedReason() {
      setBlockedReason(null);
    },
  };
}

export const appUpdateCoordinator = createAppUpdateCoordinator();
